// src/components/modals/AddCustomerModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, User, Phone, Calendar, Building, ChevronDown } from 'lucide-react'
import CustomerService from '../../services/customerService'
import BranchService from '../../services/branchService'
import { CustomerModel } from '../../utils/customerModel'

const AddCustomerModal = ({ isOpen, onClose, onCustomerCreated, restrictedBranchId = null, isBranchRestricted = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    branch: '',
    dateOfBirth: '',
    customerType: 'normal',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        mobileNumber: '',
        branch: isBranchRestricted && restrictedBranchId ? restrictedBranchId : '',
        dateOfBirth: '',
        customerType: 'normal',
        isActive: true
      })
      setErrors({})
      fetchBranches()
    }
  }, [isOpen, isBranchRestricted, restrictedBranchId])

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      setBranchesLoading(true)
      const response = await BranchService.getAllBranches()
      
      if (response?.success) {
        let branchesData = response.data?.branches || []
        let activeBranches = branchesData.filter(branch => branch.isActive)
        
        // Filter to only manager's branch if restricted
        if (isBranchRestricted && restrictedBranchId) {
          activeBranches = activeBranches.filter(branch => branch._id === restrictedBranchId)
          console.log('🔒 Filtered to manager\'s branch only for customer creation')
        }
        
        setBranches(activeBranches)
      }
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
    } finally {
      setBranchesLoading(false)
    }
  }

  // Validate mobile number (exactly 10 digits)
  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/
    return mobileRegex.test(mobile)
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'mobileNumber') {
      // For mobile number, only allow digits and limit to 10
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Customer name is required'
    }

    // Mobile number validation
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required'
    } else if (!validateMobileNumber(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be exactly 10 digits'
    }

    // Branch validation
    if (!formData.branch) {
      newErrors.branch = 'Branch selection is required'
    }

    // Date of birth validation (optional now)
    // Customer type validation
    if (!formData.customerType || !['normal', 'vip'].includes(formData.customerType)) {
      newErrors.customerType = 'Please select a valid customer type'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      const customerData = CustomerService.formatCustomerForAPI(formData)
      const response = await CustomerService.createCustomer(customerData)
      
      if (response?.success) {
        console.log('✅ Customer created successfully:', response.data)
        
        if (onCustomerCreated) {
          onCustomerCreated(response.data)
        }
        
        onClose()
      } else {
        setErrors({ general: response?.message || 'Failed to create customer' })
      }
    } catch (error) {
      console.error('❌ Error creating customer:', error)
      
      if (error.response?.status === 409) {
        setErrors({ mobileNumber: 'A customer with this mobile number already exists' })
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || error.message
        if (errorMessage.toLowerCase().includes('mobile')) {
          setErrors({ mobileNumber: errorMessage })
        } else if (errorMessage.toLowerCase().includes('name')) {
          setErrors({ name: errorMessage })
        } else {
          setErrors({ general: errorMessage })
        }
      } else {
        setErrors({ general: error.message || 'Failed to create customer' })
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            {/* <div className="p-2 bg-orange-100 rounded-lg">
              <User className="h-5 w-5 text-orange-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Add Customer</h2>
          </div>
          <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name *
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter customer name"
              className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="0778289336"
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.mobileNumber ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                maxLength="10"
              />
            </div>
            {errors.mobileNumber && <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>}
          </div>

          {/* Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-4 w-4 text-gray-400" />
              </div>
              <select
                name="branch"
                value={formData.branch}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white appearance-none ${
                  errors.branch ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                } ${isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={loading || branchesLoading || isBranchRestricted}
              >
                <option value="">Select branch</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
            {errors.branch && <p className="mt-1 text-sm text-red-600">{errors.branch}</p>}
            {branchesLoading && <p className="mt-1 text-sm text-gray-500">Loading branches...</p>}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.dateOfBirth ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                disabled={loading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>

          {/* Customer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, customerType: 'normal' }))}
                disabled={loading}
                className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  formData.customerType === 'normal'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center">
                  <User className={`h-5 w-5 mb-1 ${
                    formData.customerType === 'normal' ? 'text-orange-600' : 'text-gray-400'
                  }`} />
                  <span className="text-sm font-medium">Normal</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, customerType: 'vip' }))}
                disabled={loading}
                className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  formData.customerType === 'vip'
                    ? 'border-yellow-500 bg-gradient-to-br from-yellow-50 to-amber-50 text-yellow-800 shadow-lg shadow-yellow-100'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center">
                  <svg 
                    className={`h-5 w-5 mb-1 ${
                      formData.customerType === 'vip' ? 'text-yellow-600' : 'text-gray-400'
                    }`}
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-sm font-medium">VIP</span>
                </div>
              </button>
            </div>
            {errors.customerType && <p className="mt-1 text-sm text-red-600">{errors.customerType}</p>}
          </div>

          {/* Status Switch */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Status
                </label>
                {/* <p className="text-xs text-gray-500">
                  {formData.isActive ? 'Customer will be active and can place orders' : 'Customer will be inactive and hidden'}
                </p> */}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {/* <span className="ml-3 text-sm text-gray-700">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span> */}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Customer'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddCustomerModal